import { useState } from 'react';
import {
  customFieldDe,
  MARCA_LABEL,
  TIPO_LABEL,
  TIPO_MARCA,
  TIPO_TIENE_API,
  TIPOS,
  UBICACION_LABEL,
  type Dispositivo,
  type TipoDispositivo,
  type TipoUbicacion,
} from '@/lib/dispositivos';
import { modelosDe, useCatalogosDispositivos } from './catalogos';

/**
 * Alta manual y edición de datos.
 *
 * El alta es para lo que la importación no ve (Ring, cajetines, mandos, los
 * bridges del almacén) o para un aparato que aún no está en la cuenta. Lo que
 * no tiene API se puede dar de alta por cantidad, sin número de serie. Con API se pide el
 * ID del fabricante; sin ella, el número de serie, que es lo único que lo
 * identifica. Al editar no se cambia el tipo ni el estado: el estado va por
 * sus acciones, y cambiar el tipo sería otro aparato.
 */
interface Props {
  dispositivo?: Dispositivo;
  onGuardar: (datos: Record<string, unknown>) => Promise<void>;
  onCancelar: () => void;
}

const UBICACIONES = Object.keys(UBICACION_LABEL) as TipoUbicacion[];

export default function FormularioDispositivo({ dispositivo, onGuardar, onCancelar }: Props) {
  const { catalogos } = useCatalogosDispositivos();
  const editando = !!dispositivo;
  const [f, setF] = useState({
    tipo: (dispositivo?.tipo || 'smartlock') as TipoDispositivo,
    modelo: dispositivo?.modelo || '',
    idFabricante: dispositivo?.idFabricante || '',
    numeroSerie: dispositivo?.numeroSerie || '',
    alias: dispositivo?.alias || '',
    ubicacionTipo: 'oficina' as TipoUbicacion,
    ubicacionNombre: '',
    compraFecha: dispositivo?.compra.fecha || '',
    proveedor: dispositivo?.compra.proveedor || '',
    precio: dispositivo?.compra.precio != null ? String(dispositivo.compra.precio) : '',
    factura: dispositivo?.compra.factura || '',
    garantiaHasta: dispositivo?.compra.garantiaHasta || '',
    enlazadoA: dispositivo?.enlazadoA || '',
    notas: dispositivo?.notas || '',
    // Alta por cantidad, sin número de serie: solo para lo que no tiene API.
    sinSerie: false,
    cantidad: '1',
  });
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const set = (k: keyof typeof f, v: string | boolean) => setF((prev) => ({ ...prev, [k]: v }));
  const tieneApi = TIPO_TIENE_API[f.tipo];
  const modelos = modelosDe(catalogos, f.tipo);
  const campo = customFieldDe(f.tipo);
  const porCantidad = !editando && !tieneApi && f.sinSerie;

  const enviar = async (e: React.FormEvent) => {
    e.preventDefault();
    setGuardando(true);
    setError(null);
    try {
      const compra = {
        fecha: f.compraFecha || null,
        proveedor: f.proveedor.trim() || null,
        precio: f.precio.trim() ? Number(f.precio.replace(',', '.')) : null,
        factura: f.factura.trim() || null,
        // Sin garantía escrita, el servidor la calcula desde la compra.
        ...(f.garantiaHasta ? { garantiaHasta: f.garantiaHasta } : {}),
      };
      const comunes = {
        modelo: f.modelo || null,
        numeroSerie: f.numeroSerie.trim() || null,
        alias: f.alias.trim() || null,
        compra,
        enlazadoA: f.enlazadoA.trim() || null,
        notas: f.notas.trim() || null,
      };
      if (editando) {
        await onGuardar(comunes);
      } else if (porCantidad) {
        await onGuardar({
          ...comunes,
          numeroSerie: null,
          tipo: f.tipo,
          cantidad: Math.max(1, Math.min(100, parseInt(f.cantidad, 10) || 1)),
          ubicacion: { tipo: f.ubicacionTipo, ...(f.ubicacionNombre.trim() ? { nombre: f.ubicacionNombre.trim() } : {}) },
        });
      } else {
        await onGuardar({
          ...comunes,
          tipo: f.tipo,
          idFabricante: (tieneApi ? f.idFabricante : f.numeroSerie || f.idFabricante).trim(),
          ubicacion: { tipo: f.ubicacionTipo, ...(f.ubicacionNombre.trim() ? { nombre: f.ubicacionNombre.trim() } : {}) },
        });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se ha podido guardar');
    } finally {
      setGuardando(false);
    }
  };

  return (
    <form onSubmit={enviar} className="space-y-5">
      <fieldset className="space-y-3">
        <legend className="text-xs font-semibold text-sereno-dark/50 uppercase tracking-wider">Qué es</legend>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <label className="block">
            <span className="label">Tipo *</span>
            <select
              className="input mt-1"
              value={f.tipo}
              disabled={editando}
              onChange={(e) => { set('tipo', e.target.value); set('modelo', ''); }}
            >
              {TIPOS.map((t) => (
                <option key={t} value={t}>{MARCA_LABEL[TIPO_MARCA[t]]} · {TIPO_LABEL[t]}</option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="label">Modelo</span>
            <select className="input mt-1" value={f.modelo} onChange={(e) => set('modelo', e.target.value)}>
              <option value="">Por confirmar</option>
              {modelos.map((m) => (
                <option key={m.id} value={m.id}>{m.label}{m.wifiIntegrado ? ' (Wi-Fi)' : ''}</option>
              ))}
            </select>
          </label>
          {tieneApi ? (
            <>
              <label className="block">
                <span className="label">{f.tipo === 'bot' || f.tipo === 'hub' ? 'ID en SwitchBot' : 'ID en Nuki (smartlockId)'} *</span>
                <input
                  className="input mt-1 font-mono"
                  required
                  disabled={editando}
                  value={f.idFabricante}
                  onChange={(e) => set('idFabricante', e.target.value)}
                  placeholder={f.tipo === 'bot' || f.tipo === 'hub' ? 'C3A2B1000001' : '18446744'}
                />
              </label>
              <label className="block">
                <span className="label">Número de serie</span>
                <input className="input mt-1 font-mono" value={f.numeroSerie} onChange={(e) => set('numeroSerie', e.target.value)} />
              </label>
            </>
          ) : (
            <div className="sm:col-span-2 space-y-2">
              {!porCantidad && (
                <label className="block">
                  <span className="label">Número de serie *</span>
                  <input
                    className="input mt-1 font-mono"
                    required={!editando}
                    value={f.numeroSerie}
                    onChange={(e) => set('numeroSerie', e.target.value)}
                    placeholder="El de la etiqueta del aparato"
                  />
                  <span className="text-[11px] text-sereno-dark/40">Es lo que identifica al aparato: sin API no hay otro dato.</span>
                </label>
              )}
              {!editando && (
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={f.sinSerie} onChange={(e) => set('sinSerie', e.target.checked)} />
                  No tengo el número de serie: dar de alta varios iguales
                </label>
              )}
              {porCantidad && (
                <label className="block max-w-[10rem]">
                  <span className="label">Cantidad *</span>
                  <input
                    type="number"
                    min={1}
                    max={100}
                    required
                    className="input mt-1"
                    value={f.cantidad}
                    onChange={(e) => set('cantidad', e.target.value)}
                  />
                </label>
              )}
              {porCantidad && (
                <p className="text-[11px] text-sereno-dark/40">
                  Cada uno entra con un ID provisional ({f.tipo === 'bridge' ? 'BR-…' : '…'}) y sale en Auditoría como «falta serie» hasta que la apuntes en su ficha.
                </p>
              )}
            </div>
          )}
          <label className="block sm:col-span-2">
            <span className="label">Alias</span>
            <input className="input mt-1" value={f.alias} onChange={(e) => set('alias', e.target.value)} placeholder="Cómo lo llamáis" />
            {tieneApi && <span className="text-[11px] text-sereno-dark/40">Con API se refresca desde Nuki / SwitchBot al sincronizar.</span>}
          </label>
        </div>
        {!editando && campo && (
          <p className="text-xs text-sereno-dark/50">
            Al instalarlo, el CRM escribirá el custom field {campo} de Hostify. Si ya está en la cuenta de {MARCA_LABEL[TIPO_MARCA[f.tipo]]}, mejor usa «Sincronizar»: se crea solo con su instantánea.
          </p>
        )}
      </fieldset>

      {!editando && (
        <fieldset className="space-y-3">
          <legend className="text-xs font-semibold text-sereno-dark/50 uppercase tracking-wider">Dónde está</legend>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <label className="block">
              <span className="label">Ubicación</span>
              <select className="input mt-1" value={f.ubicacionTipo} onChange={(e) => set('ubicacionTipo', e.target.value)}>
                {UBICACIONES.map((u) => (
                  <option key={u} value={u}>{UBICACION_LABEL[u]}</option>
                ))}
              </select>
            </label>
            {f.ubicacionTipo === 'con_empleado' && (
              <label className="block">
                <span className="label">Con quién *</span>
                <input className="input mt-1" required value={f.ubicacionNombre} onChange={(e) => set('ubicacionNombre', e.target.value)} />
              </label>
            )}
          </div>
          <p className="text-[11px] text-sereno-dark/40">
            Entra al stock. Para ponerlo en una casa, guarda y usa «Instalar» en su ficha: es lo que escribe Hostify.
            Mientras no se instale nunca, la ficha lo dirá: «sin estrenar».
          </p>
        </fieldset>
      )}

      <fieldset className="space-y-3">
        <legend className="text-xs font-semibold text-sereno-dark/50 uppercase tracking-wider">Compra y garantía</legend>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <label className="block">
            <span className="label">Fecha de compra</span>
            <input type="date" className="input mt-1" value={f.compraFecha} onChange={(e) => set('compraFecha', e.target.value)} />
          </label>
          <label className="block">
            <span className="label">Proveedor</span>
            <input className="input mt-1" value={f.proveedor} onChange={(e) => set('proveedor', e.target.value)} />
          </label>
          <label className="block">
            <span className="label">Precio (€)</span>
            <input className="input mt-1" inputMode="decimal" value={f.precio} onChange={(e) => set('precio', e.target.value)} />
          </label>
          <label className="block">
            <span className="label">Nº factura</span>
            <input className="input mt-1" value={f.factura} onChange={(e) => set('factura', e.target.value)} />
          </label>
          <label className="block col-span-2">
            <span className="label">Garantía hasta</span>
            <input type="date" className="input mt-1" value={f.garantiaHasta} onChange={(e) => set('garantiaHasta', e.target.value)} />
            <span className="text-[11px] text-sereno-dark/40">En blanco: tres años desde la compra.</span>
          </label>
        </div>
      </fieldset>

      <fieldset className="space-y-3">
        <legend className="text-xs font-semibold text-sereno-dark/50 uppercase tracking-wider">Otros</legend>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <label className="block">
            <span className="label">Enlazado a (id de otro dispositivo)</span>
            <input className="input mt-1 font-mono" value={f.enlazadoA} onChange={(e) => set('enlazadoA', e.target.value)} placeholder="nuki-18446744" />
            <span className="text-[11px] text-sereno-dark/40">Un keypad con su smartlock, un bot con su hub.</span>
          </label>
          <label className="block sm:col-span-2">
            <span className="label">Notas</span>
            <textarea className="input mt-1" rows={3} value={f.notas} onChange={(e) => set('notas', e.target.value)} />
          </label>
        </div>
      </fieldset>

      {error && <p className="text-sm text-red-600">{error}</p>}

      <div className="flex justify-end gap-2 pt-2">
        <button type="button" onClick={onCancelar} className="btn-secondary">Cancelar</button>
        <button type="submit" disabled={guardando} className="btn-primary disabled:opacity-50">
          {guardando ? 'Guardando…' : editando ? 'Guardar' : porCantidad && Number(f.cantidad) > 1 ? `Dar de alta ${f.cantidad}` : 'Dar de alta'}
        </button>
      </div>
    </form>
  );
}
